import { HowToAnswer } from "../components/interactions/HowToAnswer";
/**
 * WCC360 v2 — "How often do you actually do this?" (X-HOBBY-002).
 *
 * Only served when the learner picked at least one activity from "How You
 * Spend Your Own Time". Safe to ask directly: "hardly ever now" carries no
 * shame and no socially correct answer, so the answers are honest.
 *
 * The effect is bounded and multiplicative — see `causes.ts`. It can make an
 * interest they live in count for more, and one they've drifted from count
 * for less. It cannot add work they never named, and it never filters.
 */
import { useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import {
  HOBBY_FREQUENCY_OPTIONS, isHobbyId, type HobbyFrequency,
} from "../causes";
import { passionById } from "../passions";
import { useAssessmentV2 } from "../store";
import { cn } from "@/lib/utils";

const COPY: Record<string, { title: string; subtitle: string }> = {
  "12to14": {
    title: "How often do you actually do these?",
    subtitle:
      "Be honest — there's no better answer. We just want to know what's really part of your week.",
  },
  "15to18": {
    title: "How often do you actually do these?",
    subtitle:
      "Not how often you'd like to. How often you really do. Something you do every day tells us more than something you did once.",
  },
  "18plus": {
    title: "How often do you actually do these?",
    subtitle:
      "Honestly, not aspirationally. A thing you still do weekly counts for more than a thing you loved five years ago.",
  },
};

interface Props { onNext: () => void }

export function HobbyFrequencyScreen({ onNext }: Props) {
  const { state, setPart1, band } = useAssessmentV2();
  const copy = COPY[band] ?? COPY["18plus"];

  const hobbies = useMemo(
    () =>
      (state.part1.passion_ids ?? [])
        .filter(isHobbyId)
        .map((id) => passionById(id))
        .filter((p): p is NonNullable<typeof p> => Boolean(p)),
    [state.part1.passion_ids],
  );

  const [answers, setAnswers] = useState<Record<string, HobbyFrequency>>(
    state.part1.hobby_frequency ?? {},
  );

  const complete = hobbies.every((h) => Boolean(answers[h.id]));

  const commit = () => {
    setPart1({ hobby_frequency: answers });
    onNext();
  };

  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col">
      <h2 className="font-display text-2xl md:text-3xl font-medium leading-snug">
        {copy.title}
      </h2>
      <p className="mt-3 text-muted-foreground leading-relaxed">{copy.subtitle}</p>
      <div className="mt-4"><HowToAnswer>For each activity, tap one option for how often you do it.</HowToAnswer></div>

      <div className="mt-8 space-y-6">
        {hobbies.map((h) => (
          <fieldset key={h.id} className="rounded-3xl border border-border bg-card p-5">
            <legend className="px-1 text-sm font-medium">{h.label}</legend>
            <p className="text-xs text-muted-foreground">{h.description}</p>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {HOBBY_FREQUENCY_OPTIONS.map((o) => {
                const on = answers[h.id] === o.id;
                return (
                  <button
                    key={o.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setAnswers((prev) => ({ ...prev, [h.id]: o.id }))}
                    className={cn(
                      "min-h-[56px] rounded-2xl border px-4 py-3 text-left transition-colors",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                      on
                        ? "border-primary bg-primary/10"
                        : "border-border bg-background hover:border-primary/40",
                    )}
                  >
                    <span className="block text-sm font-medium">{o.label}</span>
                    <span className="block text-xs text-muted-foreground">{o.hint}</span>
                  </button>
                );
              })}
            </div>
          </fieldset>
        ))}
      </div>

      <div className="mt-8">
        <button
          onClick={commit}
          disabled={!complete}
          className="min-h-[52px] w-full sm:w-auto px-7 rounded-2xl bg-primary text-primary-foreground
            font-medium inline-flex items-center justify-center gap-2 disabled:opacity-40
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Continue <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
        {!complete && (
          <p className="mt-3 text-xs text-muted-foreground">
            Pick one for each — it takes a few seconds and it changes what we show you.
          </p>
        )}
      </div>
    </div>
  );
}
